import { useState, useCallback, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase'; 
import { WidgetLayout, WidgetId } from '@/types'; 
import { DEFAULT_LAYOUTS } from '../utils/layoutDefaults';

/**
 * 🏛️ useDashboardLayout Hook v2.0
 * Refactored for Hybrid Scoping: 
 * Layouts are strictly Personal Partition assets (/users/{uid}).
 * Widgets inside the layout consume data based on the active UI scope.
 */
export const useDashboardLayout = (userId: string | undefined, initialLayouts?: WidgetLayout[]) => {
  // 1. DUAL-TRACK STATE
  const [layouts, setLayouts] = useState<WidgetLayout[]>(initialLayouts || DEFAULT_LAYOUTS);
  const [draftLayouts, setDraftLayouts] = useState<WidgetLayout[]>(initialLayouts || DEFAULT_LAYOUTS);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Sync with initial data if it loads later from Firestore async listeners
  useEffect(() => {
    if (initialLayouts && initialLayouts.length > 0) {
      setLayouts(initialLayouts);
      setDraftLayouts(initialLayouts);
    } else {
      // ⚠️ Ensure DEFAULT_LAYOUTS are applied if no personal layout exists yet
      setLayouts(DEFAULT_LAYOUTS);
      setDraftLayouts(DEFAULT_LAYOUTS);
    }
  }, [initialLayouts]);

  // 2. GRID INTERACTION HANDLERS
  const onLayoutChange = useCallback((newLayouts: any[]) => {
    setDraftLayouts((prevDraft) => {
      const updated = prevDraft.map((widget: WidgetLayout) => {
        const match = newLayouts.find((l) => l.i === widget.i);
        if (match) {
          return { 
            ...widget, 
            x: match.x, 
            y: match.y, 
            w: match.w, 
            h: match.h 
          };
        }
        return widget;
      });

      // 🛡️ Persistence Guard: Flag changes for the "Unsaved" telemetry
      const isChanged = JSON.stringify(updated) !== JSON.stringify(prevDraft);
      if (isChanged) setHasUnsavedChanges(true);
      
      return updated;
    });
  }, []);

  // 3. WIDGET LIFECYCLE
  const addWidget = (id: WidgetId, defaultW: number = 10, defaultH: number = 4) => {
    if (draftLayouts.find((w: WidgetLayout) => w.i === id)) return;

    const newWidget: WidgetLayout = {
      i: id,
      x: 0,
      y: Infinity, // Auto-positions at the bottom of the grid
      w: defaultW, 
      h: defaultH
    };

    setDraftLayouts((prev) => [...prev, newWidget]);
    setHasUnsavedChanges(true);
  };

  const removeWidget = (id: string) => {
    setDraftLayouts((prev) => prev.filter((w: WidgetLayout) => w.i !== id));
    setHasUnsavedChanges(true);
  };

  // 4. PERSISTENCE LAYER (STRICT PERSONAL PARTITION)
  const saveLayout = async () => {
    if (!userId) return;
    try {
      /**
       * 🛡️ PRIVACY COMPLIANCE: 
       * Dashboard configurations are individual user preferences.
       * We target the user root document to prevent layout conflicts in households.
       */
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        dashboardLayout: draftLayouts,
        updatedAt: serverTimestamp()
      });
      
      setLayouts(draftLayouts);
      setHasUnsavedChanges(false);
      setIsEditMode(false);
    } catch (error) {
      console.error("Dashboard Persistence Failure:", error);
    }
  };

  const resetLayout = () => {
    setDraftLayouts(DEFAULT_LAYOUTS);
    setHasUnsavedChanges(true);
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      // Revert to last committed layout if canceling edit
      setDraftLayouts(layouts);
      setHasUnsavedChanges(false);
    }
    setIsEditMode(!isEditMode);
  };

  return {
    layouts: isEditMode ? draftLayouts : layouts,
    isEditMode,
    hasUnsavedChanges,
    toggleEditMode,
    onLayoutChange,
    addWidget,
    removeWidget,
    saveLayout,
    resetLayout
  };
};